import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setActiveCourseData } from '@/lib/slices/course-slice';

import { RefreshIcon } from '@/assets/icons/button';

import Request from '@/services/util/request-service';
import UtilityService from '@/services/util/utility-service';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import { CourseData } from 'course-types';

export default function CourseCodeDisplay() {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const course: CourseData = useAppSelector(() => store.getState().course.activeCourseData);

  const [copyHover, setCopyHover] = useState<boolean>(false);
  const [refreshHover, setRefreshHover] = useState<boolean>(false);

  const [isOwner, setIsOwner] = useState<boolean>(false);

  useEffect(() => {
    const cookie = UserCourseStorageService.getCourseCookie();
    if (cookie) {
      setIsOwner(!!cookie.all.enterprise.find((c) => c.id == course.id));
    }
  }, [course]);

  const renderCourseCode = () => {
    if (!course.course_code?.code) {
      return null;
    }

    return (
      <div className='flex items-center gap-1 group cursor-default'>
        <p style={{ color: '#414651', textAlign: 'center' }}>Course code</p>
        <div className='relative'>
          <div
            style={{
              borderRadius: '16px',
              background: '#F5F5F5',
              boxShadow: '0 1px 3px 0 rgba(10, 13, 18, 0.10), 0 1px 2px 0 rgba(10, 13, 18, 0.06)',
              padding: '6px 12px',
              color: '#374151',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            {course.course_code?.code}
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='12'
              height='12'
              viewBox='0 0 12 12'
              fill='none'
              onClick={() => UtilityService.copyToClipboard(course.course_code?.code)}
              onMouseEnter={() => setCopyHover(true)}
              onMouseLeave={() => setCopyHover(false)}
              style={{ cursor: 'pointer' }}
            >
              <g clipPath='url(#clip0_3_238)'>
                <path
                  d='M2.5 7.5H2C1.73478 7.5 1.48043 7.39464 1.29289 7.20711C1.10536 7.01957 1 6.76522 1 6.5V2C1 1.73478 1.10536 1.48043 1.29289 1.29289C1.48043 1.10536 1.73478 1 2 1H6.5C6.76522 1 7.01957 1.10536 7.20711 1.29289C7.39464 1.48043 7.5 1.73478 7.5 2V2.5M5.5 4.5H10C10.5523 4.5 11 4.94772 11 5.5V10C11 10.5523 10.5523 11 10 11H5.5C4.94772 11 4.5 10.5523 4.5 10V5.5C4.5 4.94772 4.94772 4.5 5.5 4.5Z'
                  stroke='#6B7280'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </g>
              <defs>
                <clipPath id='clip0_3_238'>
                  <rect width='12' height='12' fill='white' />
                </clipPath>
              </defs>
            </svg>
          </div>
          {copyHover && (
            <div
              className={`absolute flex w-fit text-nowrap bg-gray-800 rounded-lg px-2 py-1 -top-8 -left-1/2 shadow-sm text-white text-xs`}
            >
              Copy code
            </div>
          )}
        </div>
        {isOwner && (
          <a
            onClick={() => generateNewCode()}
            className='relative flex flex-row items-center gap-1'
            onMouseEnter={() => setRefreshHover(true)}
            onMouseLeave={() => setRefreshHover(false)}
          >
            {refreshHover && course.course_code?.code && (
              <div
                className={`absolute flex w-fit text-nowrap bg-gray-800 rounded-lg px-2 py-1 -top-8 -left-1/4 shadow-sm text-white text-xs`}
              >
                Refresh course code
              </div>
            )}
            <RefreshIcon className='w-5 h-5 block cursor-pointer' />
          </a>
        )}
      </div>
    );
  };

  const generateNewCode = async () => {
    const response = await (
      await Request.post('admin/c/code/refresh', { courseId: course.id })
    )?.json();
    if (!response || response.error) {
      toast.error('Failed to generate new course code.');
    } else {
      const newCourseData: CourseData = {
        ...course,
        course_code: {
          code: response.code.id,
          isActive: response.code.isActive,
          expiration: response.code.expiration,
        },
      };
      dispatch(setActiveCourseData(newCourseData));
      setRefreshHover(false);
    }
  };

  return (
    <div className='my-auto pt-1'>
      {renderCourseCode()}
      {isOwner && !course.course_code?.code && (
        <a
          onClick={() => generateNewCode()}
          className='relative flex flex-row items-center gap-1'
          onMouseEnter={() => setRefreshHover(true)}
          onMouseLeave={() => setRefreshHover(false)}
        >
          <p style={{ color: '#414651', textAlign: 'center' }}>Generate Course Code</p>
          <RefreshIcon className='w-5 h-5 block cursor-pointer' />
        </a>
      )}
    </div>
  );
}
